import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import BillingBackButton from "./BillingBackButton";
import {
  listPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  setDefaultPaymentMethod,
} from "../../../hook/useAdsApi";

// Same split-Stripe-elements pattern as AdAddCredits.js's "Add Card" panel (kept consistent
// rather than extracted into a shared component, per this codebase's flat-file-first convention).
const ELEMENT_OPTIONS = {
  style: {
    base: { fontSize: "14px", color: "#374151", "::placeholder": { color: "#9ca3af" }, fontFamily: "inherit" },
    invalid: { color: "#BE1F2E" },
  },
};

export default function AdPaymentMethods() {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(true);
  const [methods, setMethods] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = () =>
    listPaymentMethods()
      .then((res) => setMethods(res?.payment_methods || []))
      .catch(() => setMethods([]));

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const handleAddCard = async () => {
    if (!stripe || !elements) {
      toast.error("Payment form is still loading, try again in a moment");
      return;
    }
    if (!cardholderName.trim()) {
      toast.error("Enter the cardholder name");
      return;
    }
    setSubmitting(true);
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: { name: cardholderName },
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      await addPaymentMethod({ payment_method_id: paymentMethod.id });
      toast.success("Card added");
      setShowAddCard(false);
      setCardholderName("");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not add card, try again");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetDefault = async (id) => {
    setBusyId(id);
    try {
      await setDefaultPaymentMethod(id);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not set default, try again");
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Remove this payment method?")) return;
    setBusyId(id);
    try {
      await removePaymentMethod(id);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not remove payment method, try again");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Payment Methods</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Manage the cards and UPI IDs saved to your billing account.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowAddCard((o) => !o)}
                  className="bg-[#095228] text-white font-semibold px-5 py-2 rounded-full text-sm hover:opacity-90 transition whitespace-nowrap"
                >
                  {showAddCard ? "Cancel" : "+ Add New Card"}
                </button>
                <BillingBackButton />
              </div>
            </div>

            {showAddCard && (
              <div className="rounded-2xl bg-gray-50 p-5 mb-5">
                <label className="block text-sm text-gray-600 mb-1">Card number</label>
                <div className="border border-gray-300 rounded-lg px-3 py-2.5 mb-3 bg-white">
                  <CardNumberElement options={ELEMENT_OPTIONS} />
                </div>
                <label className="block text-sm text-gray-600 mb-1">Cardholder name</label>
                <input
                  type="text"
                  placeholder="Name"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
                />
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Expiry date</label>
                    <div className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white">
                      <CardExpiryElement options={ELEMENT_OPTIONS} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">CVV / CVC</label>
                    <div className="border border-gray-300 rounded-lg px-3 py-2.5 bg-white">
                      <CardCvcElement options={ELEMENT_OPTIONS} />
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleAddCard}
                  disabled={submitting}
                  className="bg-[#095228] text-white font-semibold px-5 py-2 rounded-full text-sm disabled:opacity-60"
                >
                  {submitting ? "Saving..." : "Save Card"}
                </button>
              </div>
            )}

            {loading ? (
              <p className="text-sm text-gray-500">Loading payment methods...</p>
            ) : methods.length === 0 ? (
              <p className="text-sm text-gray-500">No payment methods saved yet.</p>
            ) : (
              <div className="space-y-3">
                {methods.map((method) => (
                  <div
                    key={method.id}
                    className="rounded-2xl border border-gray-200 p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-7 rounded bg-[#095228] flex items-center justify-center text-white text-[10px] font-bold">
                        {method.type === "card" ? (method.brand || "Card").slice(0, 4).toUpperCase() : "UPI"}
                      </span>
                      <div>
                        <p className="text-sm font-semibold">
                          {method.type === "card" ? `${method.brand} •••• ${method.last4}` : method.vpa_masked}
                        </p>
                        {method.is_default && (
                          <p className="text-xs text-[#095228]">Default payment method</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!method.is_default && (
                        <button
                          type="button"
                          disabled={busyId === method.id}
                          onClick={() => handleSetDefault(method.id)}
                          className="text-xs text-[#095228] font-semibold underline disabled:opacity-50"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busyId === method.id}
                        onClick={() => handleRemove(method.id)}
                        className="text-xs text-red-500 font-semibold underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
